import { supabase } from "../config/Supabase";
import slugify from "./slugify";

// Auto-links herb names in article prose to their /herbs/:slug pages — contextual,
// keyword-anchor internal links that build topic-cluster authority. Deliberately
// CONSERVATIVE to avoid bad/over-linking on YMYL content:
//   • only "distinctive" names (≥6 chars OR multi-word) so common words like
//     "sage", "hops", "rose", "rue" are never matched;
//   • first occurrence of each herb only; capped per article;
//   • whole-word, case-insensitive, original case preserved as the anchor text.
// MUST stay in sync with scripts/lib/herb-linker.js (build-time mirror).

const BLOCKLIST = new Set([]); // add ambiguous ≥6-char names here if needed
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const isLinkable = (title) => {
  const t = String(title || "").trim();
  if (BLOCKLIST.has(t.toLowerCase())) return false;
  return t.length >= 6 || t.includes(" ");
};

export const buildHerbLinkIndex = (herbs) => {
  const slugByLower = {};
  const names = [];
  for (const h of herbs || []) {
    if (!h || !h.title || !isLinkable(h.title)) continue;
    const lower = h.title.trim().toLowerCase();
    if (slugByLower[lower]) continue;
    slugByLower[lower] = slugify(h.title);
    names.push(h.title.trim());
  }
  if (!names.length) return null;
  names.sort((a, b) => b.length - a.length); // longest first (match "milk thistle" before "thistle")
  return { regex: new RegExp(`\\b(${names.map(escapeRe).join("|")})\\b`, "gi"), slugByLower };
};

// Splits text into an array of plain strings and {slug,label} link tokens.
export const segmentText = (text, index, state) => {
  if (!index || !text) return [text];
  const { regex, slugByLower } = index;
  regex.lastIndex = 0;
  const out = [];
  let last = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (state.count >= state.cap) break;
    const matched = m[0];
    const slug = slugByLower[matched.toLowerCase()];
    if (!slug || state.used.has(slug)) continue; // skip → stays as tail text
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push({ slug, label: matched });
    state.used.add(slug);
    state.count += 1;
    last = m.index + matched.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length ? out : [text];
};

// Memoized client fetch of the herb index (one query per session).
let _cache = null;
export const getHerbIndex = () => {
  if (!_cache) {
    _cache = supabase
      .from("herbs")
      .select("id,title")
      .then(({ data }) => buildHerbLinkIndex(data || []))
      .catch(() => null);
  }
  return _cache;
};
